import { Form, Input, Modal, Select, Space, Typography } from 'antd';
import { memo } from 'react';

import { CustomInputNumber } from '@app/components/CustomInputNumber';
import { usePaidModal } from '@app/components/Modals/PaidModal/hooks/usePaidModal';
import type { NPaidModal } from '@app/components/Modals/PaidModal/types';
import {
  ACQUIRING,
  CLIENT_BALANCE_MAX,
  ERROR_REQUIRED_FIELD,
} from '@app/constants';

import type { FC } from 'react';

import '@app/components/Modals/PaidModal/styles/PaidModal.desktop.scss';

const { Text } = Typography;
const { TextArea } = Input;

export const PaidModal: FC<NPaidModal.Props> = memo(
  ({
    isPaidModalOpen,
    setIsPaidModalOpen,
    handleTopClientBalance,
    IsPaidModalLoading,
    branchId,
  }) => {
    const { form, paymentAccounts, submitForm, handleDialogOpening } =
      usePaidModal(handleTopClientBalance, branchId);
    const filteredPaymentAccounts = paymentAccounts.filter(
      (account) => account.PayType !== ACQUIRING
    );

    return (
      <Modal
        className="modal top-balance_modal"
        data-testid="replenish-balance-popup"
        open={isPaidModalOpen}
        title={
          <span data-testid="replenish-balance-title">
            Пополнение баланса клиента
          </span>
        }
        afterOpenChange={(isOpened) => handleDialogOpening(isOpened)}
        okText={<span data-testid="save">Пополнить</span>}
        cancelText={<span data-testid="cancel">Отменить</span>}
        onOk={form.submit}
        okButtonProps={{
          loading: IsPaidModalLoading,
        }}
        onCancel={() => {
          setIsPaidModalOpen(false);
          form.resetFields();
        }}
        cancelButtonProps={{
          disabled: IsPaidModalLoading,
          'data-testid': 'close-popup',
        }}
      >
        <Form form={form} onFinish={submitForm} className="modal-form">
          <Space direction="vertical" size="large" className="form-space">
            <div className="container">
              <Text data-testid="amount" className="label-require" strong>
                Сумма
              </Text>

              <Form.Item
                name="Amount"
                className="mg-0"
                rules={[
                  { required: true, message: ERROR_REQUIRED_FIELD },
                  {
                    validator: (_, value) => {
                      const numericValue = parseFloat(value);
                      if (!value || isNaN(numericValue)) {
                        return Promise.reject(new Error(ERROR_REQUIRED_FIELD));
                      }
                      if (numericValue === 0) {
                        return Promise.reject(
                          new Error('Значение не может быть 0')
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <CustomInputNumber
                  className="custom-amount"
                  dataTestId="amount-input"
                  isAllowed={(floatValue: number) =>
                    floatValue === undefined ||
                    (floatValue > 0 && floatValue <= CLIENT_BALANCE_MAX)
                  }
                  onSuccess={({ value }) => {
                    form.setFieldValue('Amount', value);
                  }}
                />
              </Form.Item>
            </div>

            <div className="container">
              <Text data-testid="cashbox" className="label-require" strong>
                Касса
              </Text>

              <Form.Item
                name="PaymentAccountId"
                className="mg-0"
                rules={[
                  {
                    required: true,
                    message: ERROR_REQUIRED_FIELD,
                  },
                ]}
              >
                <Select
                  size="large"
                  data-testid="cashbox-select"
                  options={filteredPaymentAccounts.map(
                    ({ Id, Name, PayType }) => {
                      return {
                        label: (
                          <div
                            data-testid="cashbox-item"
                            className="payment-account-label"
                          >
                            <div
                              className="payment-account-label-text-ellipsis"
                              title={Name || undefined}
                            >
                              <Text data-testid="cashbox-name" className="mg-0">
                                {Name}
                              </Text>
                            </div>
                            <Text
                              data-testid="cashbox-type"
                              type="secondary"
                              className="payment-account-label-text mg-0"
                            >
                              {PayType === 'bank'
                                ? 'Расчётный счёт'
                                : 'Наличные'}
                            </Text>
                          </div>
                        ),
                        value: Id,
                      };
                    }
                  )}
                  getPopupContainer={({ parentNode }) => parentNode}
                />
              </Form.Item>
            </div>

            <div className="container ai-flex-start">
              <Text data-testid="comment" className="label-require" strong>
                Комментарий
              </Text>

              <Form.Item
                name="Comment"
                className="mg-0"
                rules={[
                  {
                    required: true,
                    message: ERROR_REQUIRED_FIELD,
                  },
                  {
                    validator: (_, value) =>
                      value && value.trim() !== ''
                        ? Promise.resolve()
                        : Promise.reject(new Error(ERROR_REQUIRED_FIELD)),
                  },
                ]}
              >
                <TextArea
                  data-testid="comment-input"
                  size="large"
                  maxLength={400}
                />
              </Form.Item>
            </div>
          </Space>
        </Form>
      </Modal>
    );
  }
);
